/** Submission status ids returned by the API (app.AccountsSubmission.Status). */
export enum SubmissionStatusId {
  Draft = 0,
  Submitting = 1,
  Pending = 2,
  Accepted = 3,
  Rejected = 4,
  Failed = 5,
}

export interface SubmissionRow {
  id: string;
  refNo: string;
  accountingPeriodId: string;
  from: string;
  to: string;
  accounts: string;
  sourceId: number;
  description: string;
  submission: string;
  status: string;
  statusId: SubmissionStatusId;
  hasAttachment: boolean;
  canDelete: boolean;
  canSubmit: boolean;
}

export interface SubmissionMessage {
  code: string;
  severity: string;
  message: string;
}

export interface SubmissionAttemptRow {
  attemptId: string;
  attemptNo: number;
  presenterId: string | null;
  email: string | null;
  startedAtUtc: string;
  completedAtUtc: string | null;
  status: string;
  statusId: SubmissionStatusId;
  submissionReference: string | null;
  filingAttemptId: string | null;
  isSandbox: boolean;
  messages: SubmissionMessage[];
}

export interface SubmissionDetail {
  id: string;
  clientId: string;
  refNo: string;
  reportName: string;
  accountingPeriodId: string;
  periodCovered: string;
  fromDate: string | null;
  toDate: string | null;
  sourceId: number;
  accounts: string;
  description: string | null;
  signDate: string | null;
  directorSignDate: string;
  accountantSignDate: string;
  companyName: string;
  companyNumber: string | null;
  location: string;
  companyAuthorisationCode: string | null;
  attachmentName: string | null;
  status: string;
  statusId: SubmissionStatusId;
  canSubmit: boolean;
  attempts: SubmissionAttemptRow[];
  /** The ID of the presenter the practice files with when none is entered; null when none is configured. Never its code. */
  practicePresenterId?: string | null;
  /** True while filing is sandboxed (nothing is sent to Companies House): the company authentication code may be 6 to 15 characters. */
  isSandbox?: boolean;
}

export interface ValidationIssueRow {
  srNo: number;
  warning: string;
  severity: string;
  isBlocking: boolean;
  link: string;
  route: string | null;
}

export interface SubmissionValidationResult {
  submissionId: string;
  canSubmit: boolean;
  blockingCount: number;
  warningCount: number;
  issues: ValidationIssueRow[];
}

export interface ImportIxbrlPayload {
  clientId: string;
  accountingPeriodId: string;
  description: string | null;
  fromDate: string | null;
  toDate: string | null;
  signDate: string | null;
  file: File;
}

export interface SubmitToCompaniesHousePayload {
  presenterId: string;
  presenterAuthentication: string;
  email: string | null;
  companyAuthorisationCode: string;
}

export interface CompaniesHouseDataset<T> {
  clientId: string;
  companyNumber: string | null;
  fetchedAtUtc: string | null;
  fetchedOn: string;
  refreshError: string | null;
  items: T[];
}

export interface CompanyOfficerRow {
  name: string;
  address: string;
  role: string;
  nationality: string;
  residence: string;
  appointed: string;
  resigned: string;
  occupation: string;
}

export interface CompanyPscRow {
  name: string;
  address: string;
  notified: string;
  ceased: string;
  residence: string;
  nationality: string;
  natures: string[];
}

export interface FilingHistoryRow {
  transactionId: string;
  date: string;
  description: string;
  category: string;
  hasDocument: boolean;
}

export interface RefreshDirectorsFailure {
  clientId: string;
  clientName: string;
  message: string;
}

export interface RefreshDirectorsResult {
  clientsChecked: number;
  clientsUpdated: number;
  directorsImported: number;
  failures: RefreshDirectorsFailure[];
}

export interface DataSecuritySettings {
  clientId: string;
  isEnabled: boolean;
  masterPassword: string | null;
  updatedAtUtc: string | null;
  /** The calling user's own permission - false shows the Edit control read-only. */
  canEdit: boolean;
}
